import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';

const SettingsDialog = ({
  open,
  onClose,
  videoInputDevices,
  audioInputDevices,
  selectedVideoDeviceId,
  selectedAudioDeviceId,
  onVideoDeviceChange,
  onAudioDeviceChange,
  videoQuality,
  audioQuality,
  onVideoQualityChange,
  onAudioQualityChange,
  isRecording,
  onRecordingChange,
  getDeviceLabel,
  isBackgroundBlurEnabled,
  isProcessorLoading,
  processorError,
  onBackgroundBlurToggle
}) => {
  const handleVideoDeviceChange = (event) => {
    const deviceId = event.target.value;
    onVideoDeviceChange(deviceId);
  };

  const handleAudioDeviceChange = (event) => {
    const deviceId = event.target.value;
    onAudioDeviceChange(deviceId);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      disableRestoreFocus
      aria-labelledby="settings-dialog-title"
      aria-describedby="settings-dialog-description"
    >
      <DialogTitle id="settings-dialog-title">Video Call Settings</DialogTitle>
      <DialogContent id="settings-dialog-description">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Camera</InputLabel>
            <Select 
              value={selectedVideoDeviceId} 
              onChange={handleVideoDeviceChange} 
              label="Camera"
            >
              {videoInputDevices.map(device => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {getDeviceLabel ? getDeviceLabel(device, 'Camera') : (device.label || `Camera ${device.deviceId}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Microphone</InputLabel>
            <Select 
              value={selectedAudioDeviceId} 
              onChange={handleAudioDeviceChange} 
              label="Microphone"
            >
              {audioInputDevices.map(device => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {getDeviceLabel ? getDeviceLabel(device, 'Microphone') : (device.label || `Mic ${device.deviceId}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Video Quality</InputLabel>
            <Select 
              value={videoQuality} 
              onChange={onVideoQualityChange} 
              label="Video Quality"
            >
              <MenuItem value="480p">480p</MenuItem>
              <MenuItem value="720p">720p</MenuItem>
              <MenuItem value="1080p">1080p</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Audio Quality</InputLabel>
            <Select 
              value={audioQuality} 
              onChange={onAudioQualityChange} 
              label="Audio Quality"
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel 
            control={
              <Switch 
                checked={isRecording} 
                onChange={onRecordingChange} 
              />
            } 
            label="Record Session" 
          />
          
          {/* Background Blur Section */}
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'rgba(103, 126, 234, 0.05)', 
            borderRadius: 1,
            border: '1px solid rgba(103, 126, 234, 0.1)'
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Background Effects
            </Typography>
            
            <FormControlLabel 
              control={
                <Switch 
                  checked={isBackgroundBlurEnabled} 
                  onChange={onBackgroundBlurToggle}
                  disabled={isProcessorLoading}
                />
              } 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">Background Blur</Typography>
                  {isProcessorLoading && (
                    <CircularProgress size={16} sx={{ color: 'rgba(103, 126, 234, 0.7)' }} />
                  )}
                </Box>
              }
            />
            
            {processorError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {processorError}
              </Alert>
            )}
            
            <Typography variant="caption" sx={{ 
              display: 'block', 
              mt: 1, 
              color: 'text.secondary',
              fontStyle: 'italic'
            }}>
              Uses AI to blur your background for privacy
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} autoFocus>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog; 